import { useEffect, useRef } from "react";
import { create, type StoreApi, type UseBoundStore } from "zustand";

interface UrlMemoryState<T> {
  value: T | null;
  set: (value: T | null) => void;
}

/**
 * Module-level singleton store for remembering a single URL param across
 * unmounts. Pair with `useUrlMemory` to keep the URL in sync.
 *
 * @example
 *   const dateMemory = createUrlMemoryStore<string>();
 *   useUrlMemory(selectedDate, setSelectedDate, dateMemory);
 */
export function createUrlMemoryStore<T>(
  initial: T | null = null,
): UseBoundStore<StoreApi<UrlMemoryState<T>>> {
  return create<UrlMemoryState<T>>((set) => ({
    value: initial,
    set: (value) => set({ value }),
  }));
}

/**
 * Bidirectional sync between a `useQueryState` URL slot and an in-memory store.
 *
 * - **First mount with empty URL:** if the store has a remembered value, push
 *   it back into the URL.
 * - **Otherwise:** every URL change is remembered.
 *
 * Net effect: navigating away from a page and back restores the user's last
 * selection, while explicit clears (URL set to null after first mount) stick.
 */
export function useUrlMemory<T>(
  urlValue: T | null,
  setUrlValue: (value: T | null) => Promise<unknown> | void,
  store: UseBoundStore<StoreApi<UrlMemoryState<T>>>,
  enabled = true,
): void {
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    if (!restoredRef.current) {
      restoredRef.current = true;
      if (urlValue == null) {
        const remembered = store.getState().value;
        if (remembered != null) {
          void setUrlValue(remembered);
          return;
        }
      }
    }
    store.getState().set(urlValue);
  }, [enabled, urlValue, setUrlValue, store]);
}
